import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export type CaseStatus =
  | "active"
  | "stayed"
  | "closed"
  | "dismissed"
  | "on_appeal";

export interface ParsedAttorney {
  name: string;
  side: "plaintiff" | "defendant" | "other";
  firm?: string | null;
  role?: string | null;
  bar_state?: string | null;
  email?: string | null;
}

export interface ParsedJudge {
  name: string;
  title?: string | null;
}

export interface ParsedMetadata {
  attorneys?: ParsedAttorney[];
  judge?: ParsedJudge | null;
  // future fields plug in here
}

export interface Case {
  id: string;
  case_number: string;
  court: string;
  jurisdiction: string;
  caption: string;
  filed_at: string;
  status: CaseStatus;
  created_at: string;
  parsed_metadata?: ParsedMetadata;
}

/**
 * Fetch the cases visible to the current user. RLS does the filtering
 * server-side via the user_can_see() predicate, so this just asks for
 * "everything" — the database returns only what the user is entitled
 * to see (their firm's cases, plus anything shared via active sharing
 * agreement, plus court_public cases they have a case role on).
 *
 * Order: most recently filed first.
 */
export function useCases() {
  return useQuery<Case[], Error>({
    queryKey: ["cases"],
    enabled: isSupabaseConfigured(),
    queryFn: async () => {
      const { data, error } = await berserkr()
        .from("cases")
        .select(
          "id, case_number, court, jurisdiction, caption, filed_at, status, created_at",
        )
        .order("filed_at", { ascending: false });

      if (error) {
        throw new Error(error.message);
      }
      return (data ?? []) as Case[];
    },
  });
}
